// Copyright 2026 AllSpice. All rights reserved. Not Gitea.
// SPDX-License-Identifier: UNLICENSED

// The per-cell provenance the chat worker publishes beside a derating CSV
// sheet (`cell_notes` on the artifact): a legend of distinct facts, each
// about a rail, a part number, the policy or one row, and a map from
// designator and field to the legend entry. The table shows an entry as a
// hover bubble; the download can fold them back into a Notes column.

import type {CsvTable} from './csv.ts';

// One value the engineer may pick for a cell the engine would not settle: a
// rating two datasheet rows state differently, or two engineers' entries.
export type CellOption = {
  value: string;
  unit?: string;
  source?: string;
};

export type CellLegendEntry = {
  scope: string;
  subject?: string;
  state: string;
  text?: string;
  source?: string;
  edited_by?: string;
  options?: CellOption[];
};

export type CellNotesColumn = {
  header: string;
  field: string | null;
  // Whether the engineer may set this column's cells and have a refresh keep
  // them; the worker marks the ratings, the operating temperature and the
  // operating values. Absent on sidecars from before the flag existed.
  editable?: boolean;
  // The unit an editable column's numbers are in, as the engine states it.
  unit?: string;
  // Whether the column is the engine's record of what it wrote (Engine Values,
  // Engine Operating Point), which a refresh reads to tell the engineer's
  // entries apart, so the table never edits it.
  read_only?: boolean;
};

export type CellNotes = {
  schema_version: number;
  component_class?: string;
  columns: CellNotesColumn[];
  legend: CellLegendEntry[];
  cells: Record<string, Record<string, number>>;
  engine_values?: Record<string, Record<string, string>>;
};

export type CellIndex = {
  referenceColumn: number;
  columnFields: Array<string | null>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// A key the record holds itself: sidecar ids are designators and field names,
// and `constructor` must not find `Object.prototype.constructor`.
function own<T>(record: Record<string, T> | undefined, key: string): T | undefined {
  return record && Object.hasOwn(record, key) ? record[key] : undefined;
}

// The engine compares an edit by value, so `6.30` back from a spreadsheet is
// still the engine's `6.3`, and a computed `0.2317021276595744680851` a
// spreadsheet saved at float precision is still the engine's number. Text
// that is not a number compares exactly.
const SAME_NUMBER_TOLERANCE = 1e-9;

export function sameNumber(a: string, b: string): boolean {
  const left = a.trim();
  const right = b.trim();
  const x = Number(left);
  const y = Number(right);
  if (left === '' || right === '' || !Number.isFinite(x) || !Number.isFinite(y)) return a === b;
  return Math.abs(x - y) <= SAME_NUMBER_TOLERANCE * Math.max(Math.abs(x), Math.abs(y));
}

// The engine offers a handful of candidates; a sidecar with more is not one
// it wrote.
const MAX_OPTIONS = 20;

// Candidates as the worker writes them (`value` is a decimal string, but a
// hand-written sidecar may carry a number). Only numbers can go into a cell,
// so anything else is dropped, and one number stated in two places is one
// choice that names both.
function normalizeOptions(raw: unknown): CellOption[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const options: CellOption[] = [];
  for (const item of raw) {
    if (!isRecord(item)) continue;
    const value = typeof item.value === 'string' ? item.value.trim() : typeof item.value === 'number' ? String(item.value) : '';
    if (value === '' || !Number.isFinite(Number(value))) continue;
    const unit = typeof item.unit === 'string' ? item.unit : '';
    const source = typeof item.source === 'string' ? item.source : '';
    const same = options.find((option) => sameNumber(option.value, value) && (option.unit ?? '') === unit);
    if (same) {
      if (source && !(same.source ?? '').split('; ').includes(source)) same.source = same.source ? `${same.source}; ${source}` : source;
      continue;
    }
    if (options.length === MAX_OPTIONS) continue;
    const option: CellOption = {value};
    if (unit) option.unit = unit;
    if (source) option.source = source;
    options.push(option);
  }
  return options.length ? options : undefined;
}

// Anything but the shape this module reads is ignored rather than trusted:
// the sidecar is data the worker wrote, and a table without bubbles beats a
// table that throws. Cell ids are not checked here; every read in
// `lookupCell` guards them.
export function parseCellNotes(raw: unknown): CellNotes | null {
  if (!isRecord(raw)) return null;
  const {columns, legend, cells} = raw;
  if (!Array.isArray(columns) || !Array.isArray(legend) || !isRecord(cells)) return null;
  if (!columns.every((column) => isRecord(column) && typeof column.header === 'string')) return null;
  if (!legend.every((entry) => isRecord(entry) && typeof entry.scope === 'string' && typeof entry.state === 'string')) return null;
  const notes = raw as unknown as CellNotes;
  return {
    ...notes,
    columns: notes.columns.map((column) => ({
      header: column.header,
      field: typeof column.field === 'string' ? column.field : null,
      ...(typeof column.editable === 'boolean' ? {editable: column.editable} : {}),
      ...(typeof column.unit === 'string' && column.unit ? {unit: column.unit} : {}),
      ...(typeof column.read_only === 'boolean' ? {read_only: column.read_only} : {}),
    })),
    legend: notes.legend.map((entry) => {
      const options = normalizeOptions(entry.options);
      const {options: _raw, ...rest} = entry;
      return options ? {...rest, options} : rest;
    }),
  };
}

// The fields an engineer may set, with their units, on a sheet from before
// sidecar columns carried `editable` and `unit`: the ratings and the operating
// temperature. Such a sheet records no operating point, so the engine does not
// keep an operating value typed into it; its next refresh adds that record.
const LEGACY_FIELD_UNITS: Record<string, string> = {
  rated_voltage: 'V', rated_power: 'W', rated_reverse_voltage: 'V', rated_forward_current: 'A', rated_surge_current: 'A',
  rated_junction_temperature: '°C', rated_operating_temperature: '°C', operating_temperature: '°C',
};

// Whether the engineer may set a column's cells, by the sidecar's own flag,
// or by the field list when the sidecar predates the flag.
export function columnEditable(notes: CellNotes, index: CellIndex, column: number): boolean {
  const field = index.columnFields[column];
  if (!field) return false;
  // A column the sidecar locks is never the engineer's, whatever else it says.
  if (notes.columns.some((item) => item.field === field && item.read_only === true)) return false;
  const flagged = notes.columns.some((item) => item.editable !== undefined);
  if (flagged) return notes.columns.some((item) => item.field === field && item.editable === true);
  return own(LEGACY_FIELD_UNITS, field) !== undefined;
}

// The engine's record columns, trimmed and lowercased as the engine matches
// them, for a sheet whose sidecar does not list them: one published before the
// flag, or a sheet with no sidecar at all.
const RECORD_HEADERS = new Set(['engine values', 'engine operating point']);

// Whether a column is the engine's own record, which no edit may touch: the
// sidecar's flag where it lists the column, the engine's names for it otherwise.
export function columnReadOnly(notes: CellNotes | null, header: string): boolean {
  const listed = notes?.columns.find((column) => column.header === header);
  if (listed) return listed.read_only === true;
  return RECORD_HEADERS.has(header.trim().toLowerCase());
}

// The unit a column's numbers are in: the sidecar's, or the field's own for a
// sidecar that predates it. A header's text is the template's and says
// nothing reliable (`Rated (Temperature)`).
export function columnUnit(notes: CellNotes, index: CellIndex, column: number): string {
  const field = index.columnFields[column];
  if (!field) return '';
  return notes.columns.find((item) => item.field === field && item.unit)?.unit ?? own(LEGACY_FIELD_UNITS, field) ?? '';
}

export type CellFlag = 'needs_input' | 'refused' | 'proposed';

// Whether a cell asks for the engineer's hand: an open gap or a proposal the
// engine wants confirmed, on a column they may set or with candidates to
// pick among. A cell they already edited asks nothing more, and a derived
// gap (a stress, a verdict) only repeats the input it waits on.
export function cellFlag(entry: CellLegendEntry, status: CellStatus, editable: boolean): CellFlag | null {
  if (status.state === 'edited') return null;
  if (entry.state !== 'needs_input' && entry.state !== 'refused' && entry.state !== 'proposed') return null;
  if (!editable && !entry.options?.length) return null;
  return entry.state;
}

// What the table hands its host when the engineer opens a flagged cell. The
// reference column and header let the host check, before it writes, that the
// sheet still has this cell where the badge found it.
export type ResolveTarget = {
  rowIndex: number;
  columnIndex: number;
  referenceColumn: number;
  reference: string;
  header: string;
  field: string | null;
  unit: string;
  value: string;
  flag: CellFlag;
  entry: CellLegendEntry;
  editable: boolean;
  // The badge the panel anchors to, follows while scrolling and returns
  // focus to.
  anchor: HTMLElement;
};

// Where the designator lives and which field each rendered column carries,
// resolved through the sidecar's own header list so a template's renamed
// columns still join.
export function buildCellIndex(notes: CellNotes, header: string[]): CellIndex {
  const fieldByHeader = new Map(notes.columns.map((column) => [column.header, column.field]));
  const columnFields = header.map((name) => fieldByHeader.get(name) ?? null);
  return {referenceColumn: columnFields.indexOf('reference'), columnFields};
}

export function lookupCell(index: CellIndex, notes: CellNotes, cells: string[], column: number): CellLegendEntry | null {
  if (index.referenceColumn < 0) return null;
  const field = index.columnFields[column];
  if (!field) return null;
  const fields = own(notes.cells, cells[index.referenceColumn] ?? '');
  const id = isRecord(fields) ? own(fields, field) : undefined;
  if (typeof id !== 'number' || !Number.isInteger(id)) return null;
  return notes.legend[id] ?? null;
}

// The number the engine wrote into a cell, when the sidecar recorded one.
export function lookupEngineValue(index: CellIndex, notes: CellNotes, cells: string[], column: number): string | undefined {
  if (index.referenceColumn < 0) return undefined;
  const field = index.columnFields[column];
  if (!field) return undefined;
  const fields = own(notes.engine_values, cells[index.referenceColumn] ?? '');
  const value = isRecord(fields) ? own(fields, field) : undefined;
  return typeof value === 'string' ? value : undefined;
}

const STATE_WORDS: Record<string, string> = {
  needs_input: 'needs input',
  refused: 'refused',
  not_applicable: 'n/a',
};

function stateLabel(state: string): string {
  const word = STATE_WORDS[state] ?? state;
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export type CellStatus = {
  // The state the cell is in now: the legend's, or `edited` when the sheet's
  // text no longer holds what the engine wrote.
  state: string;
  // The legend state an edit replaced, when it did.
  was?: string;
};

// Letters and digits only, so `N/A`, `n/a` and `not_applicable` all name the
// same gap word.
function wordKey(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function holdsGapWord(state: string, cellText: string): boolean {
  const key = wordKey(cellText);
  return key === wordKey(STATE_WORDS[state] ?? '') || key === wordKey(state);
}

// The one place that decides whether a cell was edited since the sheet was
// published, so the bubble and the cell's colour cannot disagree. A cell the
// engine gave a number is compared against that number; a gap cell against
// the gap word, tolerant of case and punctuation. A cleared cell is no edit:
// the engine keeps only numbers, so a refresh writes its own back.
export function cellStatus(entry: CellLegendEntry, cellText: string, engineValue?: string): CellStatus {
  if (entry.edited_by) return {state: 'edited'};
  const text = cellText.trim();
  if (text === '') return {state: entry.state};
  if (engineValue !== undefined) {
    return sameNumber(text, engineValue.trim()) ? {state: entry.state} : {state: 'edited', was: entry.state};
  }
  if (entry.state in STATE_WORDS && !holdsGapWord(entry.state, text)) return {state: 'edited', was: entry.state};
  return {state: entry.state};
}

// "Rail VDD_X: ", unless the text already names its subject.
function subjectLabel(entry: CellLegendEntry): string {
  if (!entry.subject || entry.text?.includes(entry.subject)) return '';
  const kind = entry.scope === 'rail' ? 'Rail' : entry.scope === 'part' ? 'Part' : '';
  return kind ? `${kind} ${entry.subject}: ` : `${entry.subject}: `;
}

// The entry's reason with its source, without the subject.
export function entryExplanation(entry: CellLegendEntry): string {
  const text = entry.text ?? '';
  if (entry.source && text) return `${text} (${entry.source})`;
  return text || entry.source || '';
}

// What a legend entry says about its cells: its subject, reason and source,
// as the bubble and the resolver both show it.
export function entryDetail(entry: CellLegendEntry): string {
  return `${subjectLabel(entry)}${entryExplanation(entry)}`;
}

export function cellBubbleText(entry: CellLegendEntry, status: CellStatus): string {
  if (entry.edited_by) return `Edited by ${entry.edited_by}`;
  const detail = entryDetail(entry);
  const lead = status.was ? `Edited (was ${STATE_WORDS[status.was] ?? status.was})` : stateLabel(entry.state);
  return detail ? `${lead} · ${detail}` : lead;
}

// The Notes lines a downloaded sheet carries for one row, in column order,
// in the "<header>: <state> - <reason>" form the worker writes for the row's
// own facts. Lines the sheet's Notes cell already holds are not repeated.
function rowNotes(index: CellIndex, notes: CellNotes, header: string[], cells: string[], existing: string): string[] {
  const lines: string[] = [];
  for (const [column, name] of header.entries()) {
    const entry = lookupCell(index, notes, cells, column);
    if (!entry) continue;
    let line: string;
    if (entry.edited_by) {
      line = `${name}: edited by ${entry.edited_by}`;
    } else {
      const word = STATE_WORDS[entry.state] ?? entry.state;
      const reason = entryExplanation(entry);
      line = reason ? `${name}: ${word} - ${reason}` : `${name}: ${word}`;
    }
    if (!existing.includes(line)) lines.push(line);
  }
  return lines;
}

export const NOTES_HEADER = 'Notes';

export function joinNotesColumn(table: CsvTable, notes: CellNotes): CsvTable {
  const index = buildCellIndex(notes, table.header);
  let notesColumn = table.header.indexOf(NOTES_HEADER);
  const header = notesColumn >= 0 ? table.header : [...table.header, NOTES_HEADER];
  if (notesColumn < 0) notesColumn = header.length - 1;
  const rows = table.rows.map((cells) => {
    const padded = cells.length < header.length ? [...cells, ...new Array<string>(header.length - cells.length).fill('')] : Array.from(cells);
    const existing = padded[notesColumn];
    const joined = rowNotes(index, notes, table.header, cells, existing).join('; ');
    padded[notesColumn] = existing && joined ? `${existing}; ${joined}` : existing || joined;
    return padded;
  });
  return {...table, header, rows};
}
